import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Calendar, Clock, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { portalAvailableSlots, portalCreateBooking } from '../services/api';

/**
 * Grouped-by-date time-chip picker for pass session bookings.
 * Fetches /portal/available-slots (auth required) and lets the client tap
 * a time chip to book. On success calls onBooked() so the dashboard reloads.
 */
const DAY_LABEL = ['V', 'H', 'K', 'Sze', 'Cs', 'P', 'Szo'];

const fmtDayLabel = (iso) => {
  const d = new Date(iso + 'T00:00:00');
  const monthDay = d.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  return { top: DAY_LABEL[d.getDay()], bottom: monthDay };
};

const isToday = (iso) => {
  const t = new Date();
  const s = t.toISOString().slice(0, 10);
  return s === iso;
};

const BookingWidget = ({ onBooked, disabled = false, disabledReason = '' }) => {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(null); // {date, time}
  const [visibleStart, setVisibleStart] = useState(0);
  const VISIBLE_COUNT = 5;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await portalAvailableSlots(21);
      setDays(data.days || []);
    } catch (err) {
      if (err?.response?.status !== 401) {
        toast.error('Az időpontok betöltése nem sikerült.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doBook = async () => {
    if (!pending) return;
    try {
      await portalCreateBooking(pending.date, pending.time);
      toast.success(`Foglalás létrehozva — ${pending.date} ${pending.time}`);
      setPending(null);
      onBooked && onBooked();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'A foglalás nem sikerült.');
      setPending(null);
    }
  };

  const visible = days.slice(visibleStart, visibleStart + VISIBLE_COUNT);
  const hasMore = visibleStart + VISIBLE_COUNT < days.length;

  return (
    <div data-testid="booking-widget" className="rounded-2xl border border-gray-200 bg-white p-5 md:p-6 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="text-yellow-700" size={18} />
          <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Új időpont foglalása
          </h3>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid="booking-prev-days"
            onClick={() => setVisibleStart((s) => Math.max(0, s - VISIBLE_COUNT))}
            disabled={visibleStart === 0}
            className="w-8 h-8 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <ChevronLeft size={16} />
          </button>
          <button
            type="button"
            data-testid="booking-next-days"
            onClick={() => setVisibleStart((s) => s + VISIBLE_COUNT)}
            disabled={!hasMore}
            className="w-8 h-8 rounded-full border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {disabled && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900 mb-4">
          {disabledReason || 'Foglalás jelenleg nem lehetséges.'}
        </div>
      )}

      {!disabled && !loading && (
        <p className="text-xs text-gray-500 mb-3" data-testid="booking-lead-time-note">
          Időpontot legalább 12 órával korábban tudsz foglalni.
        </p>
      )}

      {loading ? (
        <div className="text-center py-8">
          <Loader2 className="animate-spin text-yellow-600 mx-auto mb-2" size={24} />
          <p className="text-xs text-gray-500">Elérhető időpontok betöltése…</p>
        </div>
      ) : visible.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">
          Nincs elérhető időpont ebben az időszakban.
        </div>
      ) : (
        <div className="grid grid-cols-5 gap-2 md:gap-3">
          {visible.map((d) => {
            const lbl = fmtDayLabel(d.date);
            const today = isToday(d.date);
            return (
              <div
                key={d.date}
                data-testid={`booking-day-${d.date}`}
                className={
                  'rounded-xl border p-2 md:p-3 flex flex-col items-center min-h-[220px] ' +
                  (today ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 bg-gray-50')
                }
              >
                <div className={`text-[10px] font-bold uppercase tracking-widest ${today ? 'text-yellow-700' : 'text-gray-500'}`}>
                  {lbl.top}
                </div>
                <div className={`text-sm font-bold ${today ? 'text-yellow-800' : 'text-gray-800'} mb-2`}>
                  {lbl.bottom}
                </div>
                <div className="flex flex-col gap-1 w-full">
                  {d.times.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`booking-slot-${d.date}-${t}`}
                      onClick={() => !disabled && setPending({ date: d.date, time: t })}
                      disabled={disabled}
                      className={
                        'w-full text-xs md:text-sm font-semibold rounded-md py-1.5 transition-colors ' +
                        (disabled
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-white border border-gray-200 hover:border-yellow-500 hover:bg-yellow-50 hover:text-yellow-800 text-gray-700')
                      }
                    >
                      <Clock size={10} className="inline-block mr-0.5 -mt-px" />
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!pending} onOpenChange={(v) => !v && setPending(null)}>
        <AlertDialogContent data-testid="booking-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Foglalás megerősítése</AlertDialogTitle>
            <AlertDialogDescription>
              Foglalás időpontja: <strong>{pending?.date} {pending?.time}</strong>. A rendszer levon 1 alkalmat
              a bérletedből (függőben). Az edzés utáni napon Gábor megerősíti, hogy megjelentél.
              Lemondani legkésőbb 12 órával az időpont előtt tudsz a fiókodban.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel><X size={14} className="mr-1.5" /> Mégse</AlertDialogCancel>
            <AlertDialogAction
              data-testid="booking-confirm-yes"
              onClick={doBook}
              className="bg-yellow-600 hover:bg-yellow-700"
            >
              Igen, foglalom
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default BookingWidget;
